import { useState, useRef, useCallback, useEffect } from 'react';
import { mentorChatsApi, chatMessagesApi, MentorChat } from '@/utils/api-pb';
import { chatLogger } from '@/utils/logger';
import { toast } from 'sonner';

export interface ArtifactReference {
  type: string;
  recordId: string;
  status: 'completed' | 'failed';
  summary?: string;
}

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  artifacts?: ArtifactReference[];
}

interface UseChatMessagesOptions {
  currentChat: MentorChat | null;
  setCurrentChat: (chat: MentorChat | null) => void;
  setChats: React.Dispatch<React.SetStateAction<MentorChat[]>>;
  setTotalMessageCount: React.Dispatch<React.SetStateAction<number>>;
  isLimitReached: boolean;
  projectId?: string;
}

export function useChatMessages({
  currentChat,
  setCurrentChat,
  setChats,
  setTotalMessageCount,
  isLimitReached,
  projectId,
}: UseChatMessagesOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load messages from chat_messages collection when currentChat changes
  const loadMessages = useCallback(async (chatId: string) => {
    try {
      const result = await chatMessagesApi.list(chatId, 'mentor', 1, 200);
      setMessages(result.messages.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.created,
        artifacts: m.metadata?.artifacts,
      })));
    } catch (error) {
      console.error('Error loading chat messages:', error);
      // Fallback to chat JSON messages
      if (currentChat) {
        setMessages(currentChat.messages || []);
      }
    }
  }, [currentChat]);

  useEffect(() => {
    if (currentChat) {
      loadMessages(currentChat.id);
    } else {
      setMessages([]);
    }
  }, [currentChat?.id]);

  // Авто-скролл вниз
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Отправка сообщения
  const handleSend = async (messageText?: string, input?: string, attachments?: File[], artifactAttachments?: Array<{ id: string; type: string }>) => {
    const textToSend = messageText || (input || '').trim();
    chatLogger.debug('handleSend called:', { textToSend, isLoading, isLimitReached, currentChat: currentChat?.id });

    if (!textToSend) {
      chatLogger.debug('No text to send');
      return false;
    }
    if (isLoading) {
      chatLogger.debug('Already loading');
      return false;
    }
    if (isLimitReached) {
      chatLogger.debug('Limit reached');
      return false;
    }

    // Если нет текущего чата - создаём
    let chatToUse = currentChat;
    if (!chatToUse) {
      chatLogger.debug('No current chat, creating new one...');
      try {
        chatToUse = await mentorChatsApi.create(textToSend.split(/\s+/).slice(0, 3).join(' '));
        chatLogger.debug('Chat created:', chatToUse.id);
        setCurrentChat(chatToUse);
        setChats((prev) => [chatToUse!, ...prev]);
      } catch (error) {
        console.error('[ChatPage] Error creating chat:', error);
        toast.error('Ошибка создания чата: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        return false;
      }
    }

    const userMessage: Message = {
      role: 'user',
      content: textToSend,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      chatLogger.debug('Sending message to API...', { chatId: chatToUse.id, message: userMessage.content });
      const { reply, artifacts: responseArtifacts, chat: updatedChat } = await mentorChatsApi.sendMessage(
        chatToUse.id,
        userMessage.content,
        attachments && attachments.length > 0 ? attachments : undefined,
        artifactAttachments && artifactAttachments.length > 0 ? artifactAttachments : undefined,
        projectId,
      );
      chatLogger.debug('Response received:', { replyLength: reply?.length, artifactsCount: responseArtifacts?.length });

      const parsedArtifacts: ArtifactReference[] | undefined = responseArtifacts && responseArtifacts.length > 0
        ? responseArtifacts.map(a => ({
            type: a.type,
            recordId: a.recordId,
            status: a.status as 'completed' | 'failed',
            summary: a.summary,
          }))
        : undefined;

      const assistantMessage: Message = {
        role: 'assistant',
        content: reply,
        timestamp: new Date().toISOString(),
        artifacts: parsedArtifacts,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setCurrentChat(updatedChat);
      setTotalMessageCount((prev) => prev + 1);

      // Обновляем заголовок чата если это первое сообщение
      if (updatedChat.messages.length === 2) {
        const title = userMessage.content.split(/\s+/).slice(0, 3).join(' ');
        await mentorChatsApi.updateTitle(updatedChat.id, title);
        setChats((prev) =>
          prev.map((c) => (c.id === updatedChat.id ? { ...c, title } : c))
        );
      }
    } catch (error) {
      console.error('[ChatPage] Error sending message:', error);
      const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Произошла ошибка при отправке сообщения: ${errorMessage}. Попробуйте ещё раз.`,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }

    return true;
  };

  return {
    messages,
    setMessages,
    isLoading,
    messagesEndRef,
    handleSend,
    scrollToBottom,
  };
}
